// NeoCore · BI Créditos · Momentum (pantalla alterna)
// -----------------------------------------------------------------------------
// Tablero CEO/COO premium, institucional NeoCoop: canvas crema, naranja de marca,
// DM Sans, sombras suaves, isotipo. recharts para las visualizaciones.
// Lidera con las victorias. Fuente: RPC bi_creditos_momentum().
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowClockwise, Rocket } from '@phosphor-icons/react'
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar, PolarAngleAxis, PieChart, Pie, Cell,
  BarChart, Bar, ComposedChart, Line,
} from 'recharts'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = any
const C = {
  brand: '#FF5B04', brandSoft: '#fff1e6', cream: '#fbf7f3',
  emerald: '#16a34a', amber: '#d97706', ink: '#14171c', ink2: '#52525b',
  muted: '#9aa0a6', line: '#efece7',
}
const DISPLAY = "'DM Sans','Noto Sans',system-ui,sans-serif"
const card: React.CSSProperties = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 22, boxShadow: '0 1px 2px rgba(20,23,28,.03), 0 12px 30px -14px rgba(20,23,28,.14)' }
const cap: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: C.muted }
const GRAD_LABEL = ['', '1ª vez', '2ª vez', '3ª vez', '4ª+']
const GRAD_NOTE: Record<number, string> = {
  1: 'El debut. Acá se decide todo — donde OKID y el filtro suman más.',
  2: 'Ya probaron que pagan. El riesgo cae a la mitad.',
  3: 'Prácticamente oro. Casi nadie toca mora.',
  4: 'Perfectos. Alfombra roja y aprobación al toque.',
}
const AGE_PAL = ['#16a34a', '#86d9a6', '#f6c65b', '#f0954a', '#ec7a52', '#d64545']

function Tip({ active, payload, label, fmt }: AnyObj) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ ...card, borderRadius: 12, padding: '8px 11px', fontSize: 12, boxShadow: '0 8px 24px rgba(20,23,28,.14)' }}>
      {label != null && <div style={{ fontWeight: 700, color: C.ink, marginBottom: 3 }}>{label}</div>}
      {payload.map((p: AnyObj, i: number) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: p.color || p.fill }} />
          <span style={{ color: C.muted }}>{p.name}:</span><b style={{ color: C.ink }}>{fmt ? fmt(p.value) : p.value}</b>
        </div>
      ))}
    </div>
  )
}

export function NeoCoreBIMomentumPage() {
  const [d, setD] = useState<AnyObj>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [selG, setSelG] = useState(3)

  const load = useCallback(() => {
    setLoading(true); setErr(null)
    supabase.rpc('bi_creditos_momentum').then(({ data, error }) => {
      if (error) setErr(error.message); else setD(data)
      setLoading(false)
    })
  }, [])
  useEffect(() => { load() }, [load])

  const grad: AnyObj[] = useMemo(() => (d?.graduacion ?? []), [d])
  const selRung = useMemo(() => grad.find((g) => g.veces === selG) ?? grad[grad.length - 1], [grad, selG])

  if (loading) return <div style={{ padding: 64, textAlign: 'center', color: C.muted, fontFamily: DISPLAY }}>Cargando momentum…</div>
  if (err) return <div style={{ padding: 16 }}><div style={{ ...card, borderColor: '#f3c8bf', background: '#fdf1ee', color: '#a3402d', padding: 16 }}>{err}</div></div>
  if (!d) return null

  const h = d.hero ?? {}, w = d.wins ?? {}, op = d.oportunidad ?? {}, rec = d.recuperacion ?? {}
  const crec: AnyObj[] = (d.crecimiento ?? []).map((c: AnyObj) => ({ ...c, sem: String(c.sem).slice(5) }))
  const aging: AnyObj[] = (d.aging ?? []).filter((a: AnyObj) => a.n > 0)
  const timing: AnyObj[] = d.pago_timing ?? []
  const cosecha: AnyObj[] = (d.cosecha ?? []).map((x: AnyObj) => ({ ...x, sem: String(x.sem).slice(5), fpd: x.fpd_pct }))
  const growthX = crec.length >= 2 && crec[0].n > 0 ? crec[crec.length - 1].n / crec[0].n : null
  const timingTotal = timing.reduce((s, t) => s + t.n, 0) || 1

  return (
    <div style={{ background: C.cream, margin: '-16px', padding: '20px 24px 48px', minHeight: '100%', fontFamily: "'Noto Sans',system-ui,sans-serif" }}>
      <div style={{ maxWidth: 1040, margin: '0 auto', animation: 'ncfade .45s ease' }}>
        <style>{`@keyframes ncfade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>

        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/brand/isotipo-naranja.svg" alt="" style={{ height: 30 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
            <div>
              <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: '-.02em' }}>Créditos · Momentum</div>
              <div style={{ fontSize: 12, color: C.muted }}>Cartera de crédito emergente · hora Guatemala</div>
            </div>
          </div>
          <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 999, border: `1px solid ${C.brand}`, background: '#fff', color: C.brand, padding: '8px 15px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}><ArrowClockwise size={15} weight="bold" /> Actualizar</button>
        </div>

        {/* HERO */}
        <div style={{ ...card, padding: 26, marginBottom: 16, background: `linear-gradient(115deg, ${C.brandSoft}, #fff 58%)`, boxShadow: '0 1px 2px rgba(20,23,28,.03), 0 16px 40px -18px rgba(255,91,4,.28)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26, justifyContent: 'space-between' }}>
            <div style={{ flex: '1 1 300px' }}>
              <div style={{ ...cap, color: C.brand }}>Recaudado · crédito emergente</div>
              <div style={{ fontFamily: DISPLAY, fontSize: 46, fontWeight: 800, color: C.ink, letterSpacing: '-.03em', lineHeight: 1.05, marginTop: 4 }}>{formatQ(h.recaudado ?? 0)}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <Chip tone="emerald" text={`+${formatQ(h.interes_ganado ?? 0)} interés ganado`} />
                <Chip tone="brand" text={`${h.pct_anticipado ?? 0}% paga anticipado`} />
              </div>
              {/* mini area */}
              <div style={{ height: 96, marginTop: 14, marginLeft: -8 }}>
                <ResponsiveContainer>
                  <AreaChart data={crec} margin={{ top: 6, right: 6, left: -28, bottom: -8 }}>
                    <defs><linearGradient id="hg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.brand} stopOpacity={0.28} /><stop offset="100%" stopColor={C.brand} stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="sem" tick={{ fontSize: 9, fill: C.muted }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis hide />
                    <Tooltip content={<Tip fmt={(v: number) => `${v} créditos`} />} />
                    <Area type="monotone" dataKey="n" name="Colocados" stroke={C.brand} strokeWidth={2.5} fill="url(#hg)" dot={false} activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* gauge */}
            <div style={{ position: 'relative', width: 156, height: 156, flexShrink: 0, alignSelf: 'center' }}>
              <ResponsiveContainer>
                <RadialBarChart innerRadius="74%" outerRadius="100%" data={[{ v: Number(h.pct_a_tiempo ?? 0) }]} startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar background={{ fill: '#efece7' }} dataKey="v" cornerRadius={20} fill={C.emerald} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: DISPLAY, fontSize: 30, fontWeight: 800, color: C.emerald, lineHeight: 1 }}>{h.pct_a_tiempo ?? 0}%</div>
                  <div style={{ fontSize: 10, color: C.muted }}>pagan a tiempo</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* STAT CARDS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(168px,1fr))', gap: 12, marginBottom: 16 }}>
          <Stat label="Créditos pagados" value={String(w.creditos_pagados ?? '—')} sub={`${w.liquidaron ?? 0} asociados`} />
          <Stat label="Revolvencia" value={`${w.revolvencia_pct ?? 0}%`} sub={`${w.revolvieron ?? 0} volvieron`} accent={C.emerald} />
          <Stat label="Cartera activa" value={formatQ(w.cartera_saldo ?? 0)} sub={`${w.al_dia_pct ?? 0}% al día · PAR30 ${w.par30_pct ?? 0}%`} />
          <Stat label="Crecimiento" value={growthX ? `×${growthX.toFixed(1)}` : '—'} sub="colocación reciente" accent={C.brand} />
        </div>

        {/* RECUPERACIÓN */}
        <div style={{ ...card, padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: C.ink }}>Recuperación · cobranza</div>
            <div style={{ fontSize: 11.5, color: C.muted }}>Crédito emergente · hora Guatemala</div>
          </div>
          {(() => {
            const recu = Number(rec.recuperado_30 ?? 0), venc = Number(rec.vencido_monto ?? 0)
            const tasa = recu + venc > 0 ? Math.round((recu / (recu + venc)) * 100) : 0
            const tot = Math.max(1, recu + venc)
            return (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26, marginTop: 14, marginBottom: 16 }}>
                  <div>
                    <div style={cap}>Recuperado · últimos 30 días</div>
                    <div style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 800, color: C.emerald, letterSpacing: '-.02em' }}>{formatQ(recu)}</div>
                    <div style={{ fontSize: 11.5, color: C.muted }}>de cuotas que estaban vencidas</div>
                  </div>
                  <div style={{ alignSelf: 'center' }}>
                    <div style={cap}>Tasa de recuperación</div>
                    <div style={{ fontFamily: DISPLAY, fontSize: 24, fontWeight: 800, color: tasa >= 50 ? C.emerald : C.amber }}>{tasa}%</div>
                  </div>
                </div>
                {/* barra estado del dinero */}
                <div style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', marginBottom: 8 }}>
                  <div title={`Recuperado ${formatQ(recu)}`} style={{ width: `${(recu / tot) * 100}%`, background: C.emerald }} />
                  <div title={`Vencido pendiente ${formatQ(venc)}`} style={{ width: `${(venc / tot) * 100}%`, background: C.amber }} />
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 11.5, color: C.muted, marginBottom: 18 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: 99, background: C.emerald }} />Recuperado</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: 99, background: C.amber }} />Vencido pendiente</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
                  <MiniStat label="Vencido pendiente" value={formatQ(venc)} sub={`${rec.vencido_n ?? 0} cuotas`} accent={C.amber} />
                  <MiniStat label="En mora (saldo)" value={formatQ(rec.en_mora_saldo ?? 0)} sub="capital en riesgo" accent={C.amber} />
                  <MiniStat label="Pagado hoy" value={formatQ(rec.pagado_hoy ?? 0)} sub={`${rec.pagado_hoy_n ?? 0} cuotas`} accent={C.emerald} />
                  <MiniStat label="Vence esta semana" value={formatQ(rec.vence_sem_monto ?? 0)} sub={`${rec.vence_sem_n ?? 0} cuotas · anticipate`} />
                </div>
              </>
            )
          })()}
        </div>

        {/* GRADUATION + DETAIL */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
          <div style={{ ...card, padding: 22 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: C.ink }}>La escalera que sube sola 🪜</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 16 }}>El que vuelve, paga casi perfecto. Clic en un escalón ↓</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              {grad.map((g) => {
                const on = g.veces === selG
                return (
                  <button key={g.veces} onClick={() => setSelG(g.veces)} style={{ all: 'unset', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                    <span style={{ width: 44, fontSize: 12, fontWeight: 700, color: on ? C.emerald : C.ink }}>{GRAD_LABEL[g.veces]}</span>
                    <div style={{ position: 'relative', height: 30, flex: 1, borderRadius: 12, overflow: 'hidden', background: '#f3f1ee', boxShadow: on ? `0 0 0 2px ${C.emerald}` : 'none' }}>
                      <div style={{ height: 30, width: `${g.pct_limpio}%`, borderRadius: 12, background: 'linear-gradient(90deg,#16a34a,#34c07d)', opacity: 0.55 + g.pct_limpio / 260 }} />
                      <span style={{ position: 'absolute', left: 11, top: 7, fontSize: 12, fontWeight: 800, color: '#0a5d32' }}>{g.pct_limpio}% limpio</span>
                    </div>
                    <span style={{ width: 64, textAlign: 'right', fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{g.personas} pers.</span>
                  </button>
                )
              })}
            </div>
          </div>
          <div style={{ ...card, padding: 22, background: `linear-gradient(160deg, rgba(22,163,74,.06), #fff)` }}>
            <div style={cap}>Detalle</div>
            <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 800, color: C.ink }}>{selRung ? GRAD_LABEL[selRung.veces] : '—'}</div>
            {selRung && (
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 11 }}>
                <KV k="Personas" v={String(selRung.personas)} />
                <KV k="Nunca en mora" v={`${selRung.pct_limpio}%`} accent={C.emerald} />
                <span style={{ width: 'fit-content', borderRadius: 999, padding: '3px 11px', fontSize: 10, fontWeight: 800, background: 'rgba(22,163,74,.13)', color: C.emerald }}>{selRung.pct_limpio >= 95 ? 'ORO' : selRung.pct_limpio >= 75 ? 'SÓLIDO' : 'A CULTIVAR'}</span>
                <div style={{ fontSize: 12, color: C.ink2, marginTop: 2 }}>{GRAD_NOTE[selRung.veces]}</div>
              </div>
            )}
          </div>
        </div>

        {/* OPPORTUNITY */}
        <div style={{ ...card, padding: 20, marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, background: `linear-gradient(115deg, rgba(217,119,6,.08), #fff 62%)`, borderColor: 'rgba(217,119,6,.2)' }}>
          <div style={{ display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 14, background: 'rgba(217,119,6,.14)', color: C.amber, flexShrink: 0 }}><Rocket size={21} weight="fill" /></div>
          <div>
            <div style={{ ...cap, color: C.amber }}>Tu próxima palanca de crecimiento</div>
            <div style={{ fontSize: 14, color: C.ink, marginTop: 2 }}><b>{op.personas ?? 0} buenos pagadores</b> tomaron una sola vez y pagaron impecable — listos para reactivar. ~<b>{formatQ(op.monto_est ?? 0)}</b> dormidos esperando un mensaje.</div>
          </div>
        </div>

        {/* GROWTH + AGING */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12, marginTop: 16 }}>
          <div style={{ ...card, padding: 22 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: C.ink, marginBottom: 12 }}>Colocación semana a semana</div>
            <div style={{ height: 190 }}>
              <ResponsiveContainer>
                <AreaChart data={crec} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.brand} stopOpacity={0.3} /><stop offset="100%" stopColor={C.brand} stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f1ec" vertical={false} />
                  <XAxis dataKey="sem" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} width={34} />
                  <Tooltip content={<Tip />} />
                  <Area type="monotone" dataKey="n" name="Créditos" stroke={C.brand} strokeWidth={2.75} fill="url(#ag)" dot={{ r: 3, fill: '#fff', stroke: C.brand, strokeWidth: 2 }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ ...card, padding: 22 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: C.ink, marginBottom: 4 }}>Salud de la cartera</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 150, height: 150 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={aging} dataKey="n" nameKey="tramo" innerRadius={44} outerRadius={68} paddingAngle={2} stroke="none">
                      {aging.map((_, i) => <Cell key={i} fill={AGE_PAL[i] ?? '#ccc'} />)}
                    </Pie>
                    <Tooltip content={<Tip fmt={(v: number) => `${v} créditos`} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                {aging.map((a, i) => (
                  <div key={a.tramo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11.5 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.ink }}><span style={{ width: 9, height: 9, borderRadius: 99, background: AGE_PAL[i] ?? '#ccc' }} />{a.tramo}</span>
                    <span style={{ color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{a.n}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* COSECHA + TIMING */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12, marginTop: 16 }}>
          <div style={{ ...card, padding: 22 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: C.ink }}>Cosecha · calidad por camada</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10 }}>Barras = colocados · Línea = FPD de camadas maduras</div>
            <div style={{ height: 178 }}>
              <ResponsiveContainer>
                <ComposedChart data={cosecha} margin={{ top: 8, right: 6, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f1ec" vertical={false} />
                  <XAxis dataKey="sem" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} width={30} />
                  <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} width={28} />
                  <Tooltip content={<Tip />} />
                  <Bar yAxisId="l" dataKey="camada" name="Camada" fill="#efe9e2" radius={[6, 6, 0, 0]} />
                  <Line yAxisId="r" type="monotone" dataKey="fpd" name="FPD %" stroke={C.brand} strokeWidth={2.75} dot={{ r: 3, fill: C.brand }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ ...card, padding: 22 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: C.ink, marginBottom: 6 }}>Cuándo pagan</div>
            <div style={{ height: 178 }}>
              <ResponsiveContainer>
                <BarChart data={timing} layout="vertical" margin={{ top: 4, right: 14, left: 4, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="bucket" tick={{ fontSize: 11, fill: C.ink }} axisLine={false} tickLine={false} width={96} />
                  <Tooltip content={<Tip fmt={(v: number) => `${v} (${Math.round((v / timingTotal) * 100)}%)`} />} cursor={{ fill: '#faf7f2' }} />
                  <Bar dataKey="n" name="Pagos" radius={[0, 7, 7, 0]}>
                    {timing.map((_, i) => <Cell key={i} fill={[C.emerald, '#5cc98d', '#b8b2a8', C.amber][i] ?? '#ccc'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <p style={{ fontSize: 11, color: C.muted, marginTop: 16 }}>Actualizado: {d.generado_at ? new Date(d.generado_at).toLocaleString('es-GT') : '—'}</p>
      </div>
    </div>
  )
}

function Chip({ tone, text }: { tone: 'emerald' | 'brand'; text: string }) {
  const c = tone === 'emerald' ? C.emerald : C.brand
  return <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '4px 11px', fontSize: 11.5, fontWeight: 700, background: `${c}14`, color: c }}>{text}</span>
}
function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={cap}>{label}</div>
      <div style={{ fontFamily: DISPLAY, fontSize: 24, fontWeight: 800, color: accent ?? C.ink, letterSpacing: '-.02em', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  )
}
function MiniStat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ borderRadius: 14, background: C.cream, padding: 14 }}>
      <div style={cap}>{label}</div>
      <div style={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 800, color: accent ?? C.ink, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}
function KV({ k, v, accent }: { k: string; v: string; accent?: string }) {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: C.muted }}>{k}</span><b style={{ color: accent ?? C.ink }}>{v}</b></div>
}

export default NeoCoreBIMomentumPage
